import { useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';

import { usePrevious } from 'src/hooks';
import type {
  FilterGroups,
  FilterValue,
} from 'src/components/app/context/FilterContext';
import type { DateRangeAsStrings } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';
import { dateRangeFilterToApiString } from 'src/components/myFollowUp/MyFollowUpPage/Sidebar/helper';

import { FILTER_KEYS } from './constants';

/**
 * Calls callback when filters affecting current survey results change, not on initial mount.
 * Current dates are compared using the same whole-day boundaries as API requests.
 * Different timestamps that produce the same requested date range are treated as unchanged.
 * The benchmark/comparison period is ignored because it affects comparison results only.
 */
const useOnCurrentResultFilterChange = (
  filterGroups: FilterGroups,
  callback: () => void,
) => {
  const filters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filterGroups)
          // Missing and empty groups both mean no restriction.
          .filter(
            ([name, values]) =>
              name !== FILTER_KEYS.DATE_RANGE_COMPARISON && values.length > 0,
          )
          // Normalize current dates to the same full-day boundaries used by API requests.
          .map(([name, values]) => [
            name,
            name === FILTER_KEYS.DATE_RANGE
              ? dateRangeFilterToApiString(
                  values[0] as FilterValue<DateRangeAsStrings>,
                )
              : values.map(({ filterExpr }) => filterExpr),
          ]),
      ),
    [filterGroups],
  );
  const previousFilters = usePrevious(filters);

  useEffect(() => {
    if (previousFilters && !isEqual(previousFilters, filters)) callback();
  }, [filters, previousFilters, callback]);
};

export default useOnCurrentResultFilterChange;
